/**
 * Pure decision logic for the Eric reply card's inline booking pickers
 * (the "Tap a slot to book" strip). Extracted from TaskEricInput so the
 * route-selection and consumed-marker rules are unit-testable without
 * mounting the component.
 */

import type { SearchResult, Verb } from './types'

/** The slice of `lastDispatchContext` the slot-tap router cares about. */
export type SlotTapContext = {
  verb: Verb | null
  customer: SearchResult | null
  appointment: SearchResult | null
}

export type SlotTapRoute =
  | { lane: 'book'; customerId: string }
  | { lane: 'reschedule'; appointmentId: string }
  | { lane: 'brain' }

/**
 * Where a reply-card slot tap should go. Fully-resolved structured work
 * takes the deterministic cascade fast lane (docs/architecture.md §5) —
 * a `book` with a resolved customer, or a `reschedule` with a resolved
 * appointment, books via the existing /api/dashboard endpoints, no brain
 * round-trip. Anything unresolved falls back to the brain-dispatch
 * [CASCADE:] prompt path, which a parallel workstream is hardening —
 * the directive grammar is frozen and unchanged here.
 */
export function selectSlotTapRoute(ctx: SlotTapContext): SlotTapRoute {
  if (ctx.verb === 'book' && ctx.customer?.kind === 'customer') {
    return { lane: 'book', customerId: ctx.customer.id }
  }
  if (ctx.verb === 'reschedule' && ctx.appointment?.kind === 'appointment') {
    return { lane: 'reschedule', appointmentId: ctx.appointment.id }
  }
  return { lane: 'brain' }
}

/**
 * Consumed-marker predicate. Once a booking settles successfully for a
 * dispatch context, the slot picker rendered for that SAME context is
 * consumed — inert + visually disabled — so a stale "Tap a slot to book"
 * strip can't book a second appointment. A fresh dispatch context (new
 * id) gets a fresh, tappable picker; a consumption recorded for an OLDER
 * context never disables a newer flow's picker.
 */
export function isSlotPickerConsumed(
  contextId: number,
  consumedContextId: number | null,
): boolean {
  return consumedContextId !== null && consumedContextId === contextId
}

/**
 * Monotonic consumed-marker fold. Booking settles can land out of order —
 * a slow fast-lane mutation from an OLDER generation can resolve after a
 * NEWER generation already consumed its picker. Folding with max() means
 * recording an older consumption never moves the marker backwards, so a
 * late-settling older booking can't logically un-consume the newer
 * generation's picker. Every consuming site routes through this.
 */
export function consumeContextId(
  prev: number | null,
  contextId: number,
): number {
  return Math.max(prev ?? 0, contextId)
}
